var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, GemElement, customElement, emitter, attribute } from '../';
function angleAB(a, b, c, { clientX: x1, clientY: y1 }, { clientX: x2, clientY: y2 }, { clientX: x3, clientY: y3 }) {
    // https://en.wikipedia.org/wiki/Law_of_cosines
    // https://blog.csdn.net/z278930050/article/details/53319091
    return (Math.sign((x2 - x1) * (y3 - y2) - (y2 - y1) * (x3 - x2)) * Math.acos((Math.pow(a, 2) + Math.pow(b, 2) - Math.pow(c, 2)) / (2 * a * b)) * 180);
}
/**
 * 块级元素，如果要设置成 `contents`，则内容块要设置 `touch-action: none`
 * https://javascript.info/pointer-events#event-pointercancel
 *
 * 在移动端上，必须设置 `touch-action` 以允许滚动等原生动作
 *
 * 为什么空白区域会自动触发 `pointercancel`?
 *
 * @customElement gem-gesture
 *
 * @event pan
 * @event pinch
 * @event rotate
 * @event swipe
 * @event press
 * @event end
 *
 * @attr touch-action
 */
let GemGestureElement = class GemGestureElement extends GemElement {
    constructor() {
        super();
        this.pressed = false; // 触发 press 之后不触发其他事件
        this.pressTimer = 0;
        this.startEventMap = new Map();
        this.movesMap = new Map();
        this.addEventListener('pointerdown', this.onStart);
        this.addEventListener('pointermove', this.onMove);
        this.addEventListener('pointerup', this.onEnd);
        this.addEventListener('pointercancel', this.onEnd);
        this.addEventListener('dragstart', (evt) => evt.preventDefault());
    }
    getMoves(pointerId) {
        return this.movesMap.get(pointerId) || [];
    }
    getStartEvent(pointerId) {
        return this.startEventMap.get(pointerId);
    }
    getOtherLastmove(pointerId) {
        for (const id of this.movesMap.keys()) {
            if (id !== pointerId) {
                const moves = this.getMoves(id);
                return moves[moves.length - 1] || this.getStartEvent(id);
            }
        }
    }
    getMovementX(x, y) {
        const touchAction = this.touchAction;
        if ((touchAction.includes('pan-right') && x > 0) ||
            (touchAction.includes('pan-left') && x < 0) ||
            touchAction.includes('pan-x') ||
            (((touchAction.includes('pan-down') && y > 0) ||
                (touchAction.includes('pan-up') && y < 0) ||
                touchAction.includes('pan-y')) &&
                Math.abs(y) > Math.abs(x)) // horizontally scrolling
        ) {
            return 0;
        }
        return x;
    }
    getMovementY(x, y) {
        const touchAction = this.touchAction;
        if ((touchAction.includes('pan-down') && y > 0) ||
            (touchAction.includes('pan-up') && y < 0) ||
            touchAction.includes('pan-y') ||
            (((touchAction.includes('pan-right') && x > 0) ||
                (touchAction.includes('pan-left') && x < 0) ||
                touchAction.includes('pan-x')) &&
                Math.abs(x) > Math.abs(y)) // vertical scrolling
        ) {
            return 0;
        }
        return y;
    }
    onStart(evt) {
        evt.stopPropagation();
        this.setPointerCapture(evt.pointerId);
        this.movesMap.set(evt.pointerId, []);
        this.startEventMap.set(evt.pointerId, evt);
        if (evt.isPrimary) {
            this.pressed = false;
            this.pressTimer = window.setTimeout(() => {
                this.press(null);
                this.pressed = true;
            }, 251);
        }
    }
    onMove(evt) {
        evt.stopPropagation();
        const { pointerId, clientX, clientY, isPrimary, pointerType, timeStamp } = evt;
        if (!this.pressed && this.hasPointerCapture(pointerId)) {
            const moves = this.getMoves(pointerId);
            const startEvent = this.getStartEvent(pointerId);
            const lastMove = moves[moves.length - 1] || startEvent;
            // https://bugs.webkit.org/show_bug.cgi?id=220194
            // https://bugs.chromium.org/p/chromium/issues/detail?id=1092358
            const movementX = clientX - lastMove.clientX;
            const movementY = clientY - lastMove.clientY;
            const move = {
                x: this.getMovementX(movementX, movementY),
                y: this.getMovementY(movementX, movementY),
                clientX,
                clientY,
                timeStamp,
                isPrimary,
                pointerId,
            };
            moves.push(move);
            this.pan(move);
            if (this.movesMap.size !== 1) {
                const secondaryPoint = this.getOtherLastmove(pointerId);
                const moveLen = Math.sqrt(Math.pow(movementX, 2) + Math.pow(movementY, 2));
                const distanceLen = Math.sqrt(Math.pow((lastMove.clientX - secondaryPoint.clientX), 2) + Math.pow((lastMove.clientY - secondaryPoint.clientY), 2));
                const newDistanceLen = Math.sqrt(Math.pow((clientX - secondaryPoint.clientX), 2) + Math.pow((clientY - secondaryPoint.clientY), 2));
                const x = (lastMove.clientX + secondaryPoint.clientX) / 2;
                const y = (lastMove.clientY + secondaryPoint.clientY) / 2;
                this.pinch({ x, y, scale: newDistanceLen / distanceLen });
                this.rotate({ x, y, rotate: angleAB(newDistanceLen, distanceLen, moveLen, secondaryPoint, lastMove, move) });
            }
            const accuracy = pointerType === 'touch' ? 5 : 0;
            if (Math.abs(movementX) > accuracy ||
                Math.abs(movementY) > accuracy ||
                Math.abs(move.clientX - startEvent.clientX) > accuracy ||
                Math.abs(move.clientY - startEvent.clientY) > accuracy) {
                window.clearTimeout(this.pressTimer);
            }
        }
    }
    onEnd(evt) {
        evt.stopPropagation();
        const { pointerId } = evt;
        window.clearTimeout(this.pressTimer);
        if (this.movesMap.size === 1) {
            this.end(null);
        }
        // auto release: https://javascript.info/pointer-events#pointer-capturing
        if (!this.pressed && this.hasPointerCapture(pointerId)) {
            const moves = this.getMoves(pointerId);
            if (moves.length > 2) {
                const { x, y, timeStamp, clientX, clientY } = moves[moves.length - 1];
                const move2 = moves[moves.length - 3];
                if (Math.abs(x) > 2) {
                    if (Math.abs(x) > Math.abs(y)) {
                        const speed = Math.abs(move2.clientX - clientX) / (timeStamp - move2.timeStamp);
                        if (x > 0) {
                            this.swipe({ direction: 'right', speed });
                        }
                        else {
                            this.swipe({ direction: 'left', speed });
                        }
                    }
                }
                if (Math.abs(y) > 2) {
                    const speed = Math.abs(move2.clientY - clientY) / (timeStamp - move2.timeStamp);
                    if (Math.abs(y) > Math.abs(x)) {
                        if (y > 0) {
                            this.swipe({ direction: 'bottom', speed });
                        }
                        else {
                            this.swipe({ direction: 'top', speed });
                        }
                    }
                }
            }
        }
        this.movesMap.delete(pointerId);
        this.startEventMap.delete(pointerId);
    }
    render() {
        return html `
      <style>
        :host {
          display: block;
          user-select: none;
          touch-action: ${this.touchAction || 'none'};
        }
      </style>
      <slot></slot>
    `;
    }
};
__decorate([
    emitter
], GemGestureElement.prototype, "pan", void 0);
__decorate([
    emitter
], GemGestureElement.prototype, "pinch", void 0);
__decorate([
    emitter
], GemGestureElement.prototype, "rotate", void 0);
__decorate([
    emitter
], GemGestureElement.prototype, "swipe", void 0);
__decorate([
    emitter
], GemGestureElement.prototype, "press", void 0);
__decorate([
    emitter
], GemGestureElement.prototype, "end", void 0);
__decorate([
    attribute
], GemGestureElement.prototype, "touchAction", void 0);
GemGestureElement = __decorate([
    customElement('gem-gesture')
], GemGestureElement);
export { GemGestureElement };
//# sourceMappingURL=gesture.js.map